import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import type { PatchProgramsIdMutationRequest } from '@eureca/recruiter-api-sdk/types';
import {
  clearProgramDraft,
  getPendingCoverFile,
  loadProgramDraft,
  setPendingNameError,
} from '../program-create-draft';
import type { FaqDialogMode } from './AddFaqDialog';
import {
  ProgramFaqsPage as ProgramFaqsPageComponent,
  type ProgramFaq,
} from './ProgramFaqsPage.component';
import { ProgramCreateError, createProgramFull } from './programs-mutation';
import { useQuery } from '@tanstack/react-query';
import { programDefaultFaqsCatalogQueryOptions } from '@/shared/default-faqs/default-faqs-queries';
import {
  getPendingHiddenDefaultFaqIds,
  setPendingHiddenDefaultFaqIds,
} from '../program-create-draft';

function createFaqId() {
  return `faq_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Build the PATCH payload that captures the 4 rich sub-resources the Core
 * POST doesn't accept (workCondition, benefits, additional payments, FAQs).
 * Omits keys when the user didn't pick anything — keeps the diff Core stores
 * minimal and avoids overwriting nulls explicitly.
 */
function buildCreatePatch(
  draft: ReturnType<typeof loadProgramDraft>,
  faqs: ProgramFaq[]
): PatchProgramsIdMutationRequest | null {
  const target =
    draft.targetRegistrations && /^\d+$/.test(draft.targetRegistrations)
      ? Number.parseInt(draft.targetRegistrations, 10)
      : null;
  const hasWorkCondition = Boolean(draft.contractType) || target !== null;
  const patch: PatchProgramsIdMutationRequest = {};
  if (hasWorkCondition) {
    patch.workCondition = {
      contractTypeKey: draft.contractType ?? null,
      subscriberTarget: target,
    };
  }
  if (draft.selectedBenefits?.length) {
    patch.benefitNames = draft.selectedBenefits;
  }
  if (draft.selectedAdditionalPayments?.length) {
    patch.additionalPaymentNames = draft.selectedAdditionalPayments;
  }
  if (faqs.length) {
    // Strip the client-side `id` — Core mints its own UUIDs.
    patch.faqs = faqs.map((f) => ({ question: f.question, answer: f.answer }));
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

export default function ProgramFaqsPage() {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState<ProgramFaq[]>([]);

  const mutation = useMutation({
    mutationFn: async () => {
      const draft = loadProgramDraft();
      if (!draft.companyId) {
        throw new ProgramCreateError('companyId não encontrado no rascunho', 400);
      }
      return createProgramFull({
        base: {
          companyId: draft.companyId,
          statusId: 1,
          name: draft.name ?? '',
          description: draft.description ?? null,
          startApplying: draft.startDate ?? null,
          endApplying: draft.endDate ?? null,
        },
        patch: buildCreatePatch(draft, faqs),
        coverFile: getPendingCoverFile(),
        hiddenDefaultFaqIds: getPendingHiddenDefaultFaqIds(),
      });
    },
    onSuccess: () => {
      clearProgramDraft();
      void navigate({ to: '/programas/novo/sucesso' });
    },
    onError: (err) => {
      if (err instanceof ProgramCreateError) {
        // RN-03 server-side gate — name collides with an active program in
        // the same company. The SPA's pre-flight at Step 1 usually catches
        // this, but the BFF check is authoritative. When it fires from the
        // Step 3 submit, route the consultor back to Step 1 with the same
        // inline-error UX they'd have gotten from the pre-flight (the only
        // place the "Nome do programa" field exists in the form).
        if (err.code === 'duplicate_program_name') {
          setPendingNameError(
            'Esse nome já está em uso em um programa ativo. Crie um diferente, por favor!'
          );
          void navigate({ to: '/programas/novo' });
          return;
        }
        // Phase-aware messaging: when the base POST sucedeu (programa existe
        // em Rascunho) mas PATCH ou cover falharam, o consultor vê uma mensagem
        // que reflete o estado real e não "Não foi possível criar" (que sugere
        // erro de tudo).
        if (err.phase === 'patch') {
          toast.error(
            'Programa criado, mas alguns dados não foram salvos. Edite o programa para completar.'
          );
          if (err.programId) {
            void navigate({
              to: '/programas/$programId/editar',
              params: { programId: err.programId },
            });
          }
          return;
        }
        if (err.phase === 'default-faqs') {
          toast.error(
            'Programa criado, mas as FAQs padrão não foram aplicadas. Ajuste pelo edit do programa.'
          );
          if (err.programId) {
            void navigate({
              to: '/programas/$programId/editar',
              params: { programId: err.programId },
            });
          }
          return;
        }
        if (err.phase === 'cover') {
          toast.error(
            'Programa criado, mas a capa não foi enviada. Tente novamente pelo edit do programa.'
          );
          if (err.programId) {
            void navigate({
              to: '/programas/$programId/editar',
              params: { programId: err.programId },
            });
          }
          return;
        }
        if (err.status === 422) {
          toast.error('Dados inválidos. Verifique as informações e tente novamente.');
          return;
        }
      }
      toast.error('Não foi possível criar o programa. Tente novamente.');
    },
  });
  const [isDefaultFaqsOpen, setIsDefaultFaqsOpen] = useState(false);
  // EU-641 — CRIAÇÃO: catálogo puro (o programa não existe) + ocultas em
  // MEMÓRIA (module store do draft) até o POST (FQC-014); abandono descarta
  // (FQC-015 — clearProgramDraft zera).
  const defaultFaqsCatalog = useQuery({
    ...programDefaultFaqsCatalogQueryOptions(),
    enabled: isDefaultFaqsOpen,
  });
  const [hiddenDefaultFaqIds, setHiddenDefaultFaqIds] = useState<number[]>(() =>
    getPendingHiddenDefaultFaqIds()
  );
  const defaultFaqItems = (defaultFaqsCatalog.data?.items ?? []).map((item) => ({
    id: item.id,
    question: item.question,
    answer: item.answer,
    hidden: hiddenDefaultFaqIds.includes(item.id),
  }));
  function handleDefaultFaqToggle(faqId: number, hidden: boolean) {
    setHiddenDefaultFaqIds((prev) => {
      const next = hidden
        ? Array.from(new Set([...prev, faqId]))
        : prev.filter((id) => id !== faqId);
      setPendingHiddenDefaultFaqIds(next);
      return next;
    });
  }
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<FaqDialogMode>('add');
  const [editingFaqId, setEditingFaqId] = useState<string | null>(null);
  const [newFaqQuestion, setNewFaqQuestion] = useState('');
  const [newFaqAnswer, setNewFaqAnswer] = useState('');

  function openDialogInAddMode() {
    setDialogMode('add');
    setEditingFaqId(null);
    setNewFaqQuestion('');
    setNewFaqAnswer('');
    setIsAddDialogOpen(true);
  }

  function handleAddFaq() {
    openDialogInAddMode();
  }

  function handleEditFaq(id: string) {
    const faq = faqs.find((item) => item.id === id);
    if (!faq) return;
    setDialogMode('edit');
    setEditingFaqId(id);
    setNewFaqQuestion(faq.question);
    setNewFaqAnswer(faq.answer);
    setIsAddDialogOpen(true);
  }

  function handleRemoveFaq(id: string) {
    setFaqs((prev) => prev.filter((faq) => faq.id !== id));
  }

  function handleAddDialogOpenChange(open: boolean) {
    setIsAddDialogOpen(open);
  }

  function handleSaveNewFaq() {
    const question = newFaqQuestion.trim();
    const answer = newFaqAnswer.trim();
    if (!question || !answer) return;

    if (dialogMode === 'edit' && editingFaqId) {
      setFaqs((prev) =>
        prev.map((faq) => (faq.id === editingFaqId ? { ...faq, question, answer } : faq))
      );
    } else {
      setFaqs((prev) => [...prev, { id: createFaqId(), question, answer }]);
    }

    setIsAddDialogOpen(false);
    setEditingFaqId(null);
  }

  function handleViewDefaultFaqs() {
    setIsDefaultFaqsOpen(true);
  }

  function handleBack() {
    void navigate({ to: '/programas/novo/beneficios' });
  }

  function handleSubmit() {
    mutation.mutate();
  }

  return (
    <ProgramFaqsPageComponent
      faqs={faqs}
      onAddFaq={handleAddFaq}
      onEditFaq={handleEditFaq}
      onRemoveFaq={handleRemoveFaq}
      onViewDefaultFaqs={handleViewDefaultFaqs}
      isDefaultFaqsOpen={isDefaultFaqsOpen}
      defaultFaqItems={defaultFaqItems}
      defaultFaqsLoading={defaultFaqsCatalog.isLoading}
      defaultFaqsError={defaultFaqsCatalog.isError}
      onDefaultFaqsRetry={() => void defaultFaqsCatalog.refetch()}
      onDefaultFaqToggle={handleDefaultFaqToggle}
      onDefaultFaqsOpenChange={setIsDefaultFaqsOpen}
      isAddDialogOpen={isAddDialogOpen}
      onAddDialogOpenChange={handleAddDialogOpenChange}
      dialogMode={dialogMode}
      newFaqQuestion={newFaqQuestion}
      newFaqAnswer={newFaqAnswer}
      onNewFaqQuestionChange={setNewFaqQuestion}
      onNewFaqAnswerChange={setNewFaqAnswer}
      onSaveNewFaq={handleSaveNewFaq}
      progress={90}
      onBack={handleBack}
      onSubmit={handleSubmit}
      isSubmitting={mutation.isPending}
    />
  );
}
